/**
 * ShopSalesCompany Component
 * --
 * 작성자 - Owen
 * 작성일 - 2025 07 15 화요일
 * 마지막 수정일 - 2025 07 15 화요일
 */
import React from 'react';
import dayjs from 'dayjs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Col, Content, Row, Title } from 'components';
import { Divider, Flex, Table } from 'antd';
import { stringToMoneyFormat } from 'utils';

const ShopSalesCompany = (props) => {
  /**
   * SECTION - Init
   */
  const COLORS = [
    '#8884d8',
    '#82CA9D',
    '#FFC658',
    '#1677FF',
    '#FF8042',
    '#8DD1E1',
    '#83A6ED',
  ];
  /**!SECTION - Init */

  /**
   * SECTION - Props
   */
  const {
    shops = [],
    period = 7,
    orders = [],
    delivery = [],
    subscription = [],
    rental = [],
  } = props;
  /**!SECTION - Props */

  /**
   * SECTION - Route
   */

  /**!SECTION - Route */

  /**
   * SECTION - Variable
   */

  /**!SECTION - Variable */

  /**
   * SECTION - Query Data
   */

  /**!SECTION - Query Data */

  /**
   * SECTION - Mutate
   */

  /**!SECTION - Mutate */

  /**
   * SECTION - State
   */

  /**!SECTION - State */

  /**
   * SECTION - Function
   */

  /**!SECTION - Function */

  /**
   * SECTION - Hooks
   */
  // 날짜 배열 생성
  const PeriodDate = React.useMemo(() => {
    const dateFormat = 'YYYY-MM-DD';

    return new Array(period)
      .fill(null)
      .map((_, index) => dayjs().subtract(index, 'day').format(dateFormat));
  }, [period]);

  // 테이블 컬럼 설정
  const TableColumns = React.useMemo(() => {
    const dateColumns = PeriodDate.map((date) => ({
      title: (
        <Flex vertical>
          <span>{date}</span>
          <span>총매출(순매출)</span>
        </Flex>
      ),
      dataIndex: date,
      key: date,
      render: (item) => (
        <Flex vertical>
          <span style={{ fontWeight: 700 }}>
            {stringToMoneyFormat(item.totalAmount) ?? 0}
          </span>
          <span style={{ color: '#666666' }}>
            ({stringToMoneyFormat(item.totalNetAmount) ?? 0})
          </span>
        </Flex>
      ),
      align: 'center',
    }));

    return [
      {
        title: <span>업체\날짜</span>,
        dataIndex: 'shop',
        key: 'shop',
        width: 150,
        align: 'center',
      },
      ...dateColumns,
    ];
  }, [PeriodDate]);

  // 테이블 데이터 정제
  const TableData = React.useMemo(() => {
    const newList = delivery.concat(subscription).concat(rental);

    // 업체별 판매 데이터 정제
    const salesMap = new Map();
    newList.forEach((item) => {
      const { shop_information_id } = item;

      if (salesMap.has(shop_information_id)) {
        const prev = salesMap.get(shop_information_id);

        prev.push(item);
      } else {
        salesMap.set(shop_information_id, [item]);
      }
    });

    // 업체별 ShopOrder 데이터 정제
    const shopOrderMap = new Map();
    orders.forEach((item) => {
      const { shop_information_id } = item;

      if (shopOrderMap.has(shop_information_id)) {
        const prev = shopOrderMap.get(shop_information_id);

        prev.push(item);
      } else {
        shopOrderMap.set(shop_information_id, [item]);
      }
    });

    const newData = shops.map((shop) => {
      const { shop_information_id, shop_information_name } = shop;

      const sales = salesMap.get(shop_information_id) ?? [];
      const shopOrder = shopOrderMap.get(shop_information_id) ?? [];

      const salesData = PeriodDate.reduce((acc, cur) => {
        const start = dayjs(cur).startOf('day').unix();
        const end = dayjs(cur).endOf('day').unix();

        // 해당 날짜에 맞는 ShopOrder 필터
        const filteredShopOrder = shopOrder.filter(
          (item) =>
            item.payment_completion_date >= start &&
            item.payment_completion_date <= end
        );

        // 해당 날짜에 맞는 Parcel, Rental 필터
        const filtered = sales.filter((item) => {
          const { type } = item;

          if (type === 'DELIVERY' || type === 'STORE_PICKUP') {
            return item?.payment_date >= start && item?.payment_date <= end;
          } else if (type === 'SUBSCRIPTION') {
            return (
              item.regular_payment_info?.payment_date >= start &&
              item.regular_payment_info?.payment_date <= end
            );
          } else {
            return (
              item.rental_payment_info?.payment_date >= start &&
              item.rental_payment_info?.payment_date <= end
            );
          }
        });

        // 총 배송비, 사용 포인트
        const summary1 = filteredShopOrder.reduce(
          (acc2, cur2) => {
            acc2.totalDeliveryAmount += cur2.final_delivery_price;
            acc2.totalPointAmount += Math.abs(cur2.use_point);
            return acc2;
          },
          { totalDeliveryAmount: 0, totalPointAmount: 0 }
        );

        // Parcel, Rental별 결제금액, 할인금액, 환불금액 계산
        const summary2 = filtered.reduce(
          (acc2, cur2) => {
            const { type, refund } = cur2;

            if (refund) {
              acc2.totalRefundAmount += refund.refund_amount;
            }

            if (type === 'DELIVERY' || type === 'STORE_PICKUP') {
              acc2.totalPaymentAmount += cur2.payment_price;
              acc2.totalDiscountAmount += Math.abs(cur.discount_price ?? 0);
            } else if (type === 'SUBSCRIPTION') {
              acc2.totalPaymentAmount += cur2.regular_payment_info.price;
              acc2.totalDiscountAmount +=
                cur2.regular_payment_info.regular_payment_id === 1
                  ? Math.abs(cur2?.discount_price) ?? 0
                  : Math.abs(cur2?.regular_payment_info?.discount_price ?? 0);
            } else {
              acc2.totalPaymentAmount += cur2.rental_payment_info.price;
              acc2.totalDiscountAmount +=
                cur2.rental_payment_info.regular_payment_id === 1
                  ? Math.abs(cur2?.discount_price) ?? 0
                  : Math.abs(cur2?.rental_payment_info?.discount_price ?? 0);
            }

            return acc2;
          },
          {
            totalRefundAmount: 0,
            totalDiscountAmount: 0,
            totalPaymentAmount: 0,
          }
        );

        acc[cur] = {
          totalAmount:
            summary2.totalPaymentAmount +
            summary2.totalDiscountAmount +
            summary1.totalPointAmount +
            summary1.totalDeliveryAmount,
          totalNetAmount:
            summary2.totalPaymentAmount -
            summary2.totalRefundAmount +
            summary1.totalDeliveryAmount,
        };
        return acc;
      }, {});

      return {
        shop: shop_information_name,
        ...salesData,
      };
    });

    return newData;
  }, [shops, orders, delivery, subscription, rental, PeriodDate]);

  // 차트 데이터 정제
  const ChartData = React.useMemo(() => {
    if (!TableData.length) return [];

    const newData = TableData.map((item) => {
      const { shop } = item;

      const totalNetInfo = PeriodDate.reduce((acc, cur) => {
        acc[cur] = item[cur].totalNetAmount;
        return acc;
      }, {});

      const totalAmt = Object.values(totalNetInfo).reduce(
        (acc, cur) => (acc += cur),
        0
      );

      return {
        name: shop,
        ...totalNetInfo,
        amt: totalAmt,
      };
    });

    return newData;
  }, [TableData, PeriodDate]);
  /**!SECTION - Hooks */

  /**
   * SECTION - Render
   */
  return (
    <Content maxWidth={'100%'} padding={20}>
      <Row gutter={[16, 16]}>
        <Col x={24}>
          <Title size={5}>업체별 매출</Title>
          <Divider style={{ margin: '12px 0px' }} />
        </Col>
        {/* 차트 */}
        <Col x={10}>
          <Content
            maxWidth={'100%'}
            padding={0}
            style={{ overflowY: 'scroll' }}
          >
            <ResponsiveContainer width={50 * shops.length} height={500}>
              <BarChart
                width={'100%'}
                height={500}
                data={ChartData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
                barSize={20}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                {PeriodDate.map((item, index) => (
                  <Bar
                    key={`bar-${item}-${index}`}
                    dataKey={item}
                    stackId={'a'}
                    fill={COLORS[index] ?? COLORS[0]}
                    minPointSize={5}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Content>
        </Col>

        {/* 테이블 */}
        <Col x={14}>
          <Table
            size="small"
            bordered
            columns={TableColumns}
            dataSource={TableData}
            scroll={{ x: 150 * period, y: 400 }}
            pagination={false}
            summary={(pageData) => {
              const summaryData = PeriodDate.reduce((acc, cur) => {
                const sum = pageData.reduce(
                  (acc2, cur2) => {
                    acc2.totalAmount += cur2[cur].totalAmount;
                    acc2.totalNetAmount += cur2[cur].totalNetAmount;
                    return acc2;
                  },
                  { totalAmount: 0, totalNetAmount: 0 }
                );

                acc[cur] = sum;

                return acc;
              }, {});

              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} align="center">
                      <span style={{ fontWeight: 700 }}>
                        합계 ({shops.length})
                      </span>
                    </Table.Summary.Cell>
                    {Object.values(summaryData).map((item, index) => (
                      <Table.Summary.Cell
                        key={`summary-${index}`}
                        index={index + 1}
                        align="center"
                      >
                        <Flex vertical>
                          <span style={{ fontWeight: 700 }}>
                            {stringToMoneyFormat(item.totalAmount) ?? 0}
                          </span>
                          <span style={{ color: '#666666' }}>
                            ({stringToMoneyFormat(item.totalNetAmount) ?? 0})
                          </span>
                        </Flex>
                      </Table.Summary.Cell>
                    ))}
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Col>
      </Row>
    </Content>
  );
  /**!SECTION - Render */
};

export default ShopSalesCompany;
